import { type DealFormDataWithTargeting, dealFormField } from '@openx/types/deal.ts';
import { useWatchDebounce } from '@openx/utils/hooks/useWatchDebounce.ts';
import { useEffect } from 'react';
import type { Control, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { useDealContext } from '../../utils/context/DealContext.tsx';

type Params = {
  control: Control<DealFormDataWithTargeting>;
  setValue: UseFormSetValue<DealFormDataWithTargeting>;
  trigger: UseFormTrigger<DealFormDataWithTargeting>;
  historyMode?: boolean;
};

export const useUpdateDealGroupMeta = ({ control, setValue, trigger, historyMode = false }: Params) => {
  const { useDealGroupFetch } = useDealContext();

  const dealId = useWatch<DealFormDataWithTargeting, typeof dealFormField.DEAL_ID>({
    name: dealFormField.DEAL_ID,
    control,
  });
  const externalDealId = useWatchDebounce<DealFormDataWithTargeting, typeof dealFormField.EXTERNAL_DEAL_ID>({
    name: dealFormField.EXTERNAL_DEAL_ID,
    control,
  });
  const customDealId = useWatchDebounce<DealFormDataWithTargeting, typeof dealFormField.CUSTOM_DEAL_ID>({
    name: dealFormField.CUSTOM_DEAL_ID,
    control,
  });

  const currentDealId = dealId || externalDealId || customDealId || '';

  const { group, loading } = useDealGroupFetch(currentDealId, historyMode);
  useEffect(() => {
    if (!currentDealId) {
      setValue(dealFormField.METADATA_GROUP_DEAL, null, { shouldDirty: false, shouldValidate: true });
      trigger(dealFormField.METADATA);
      return;
    }

    if (!loading && group) {
      setValue(dealFormField.METADATA_GROUP_DEAL, group, { shouldDirty: false, shouldValidate: true });
      trigger(dealFormField.METADATA);
    }
  }, [currentDealId, group, loading, setValue, trigger]);
};
